import {AnimatePresence, motion} from 'framer-motion'
import type {ReactNode} from 'react'
import {useCallback, useContext, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {OPACITY_0, OPACITY_1} from 'src/common/constants'
import {PlusButton} from 'src/common/PlusButton'
import {DrawerContext} from 'src/context/DrawerContext'

const DRAWER_VARIANTS = {
  hidden: {x: '100%'},
  visible: {x: 0},
  exit: {x: '100%'},
}

type Props = {
  children: ReactNode
  isOpen?: boolean
  setIsOpen?: (open: boolean) => void
}

const Drawer = ({children, isOpen: isOpenProp, setIsOpen: setIsOpenProp}: Props): JSX.Element => {
  const context = useContext(DrawerContext)
  const [mounted, setMounted] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)

  const noopSetIsDrawerOpen = useCallback(() => {}, [])

  const isDrawerOpen = isOpenProp ?? context?.isDrawerOpen ?? false
  const setIsDrawerOpen = setIsOpenProp ?? context?.setIsDrawerOpen ?? noopSetIsDrawerOpen

  // Mount on client side
  useEffect(() => {
    setMounted(true)
  }, [])

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.isTrusted && drawerRef.current && !drawerRef.current.contains(event.target as Node) && isDrawerOpen) {
        setIsDrawerOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [drawerRef, setIsDrawerOpen, isDrawerOpen])

  // Disable scrolling while opened
  useEffect(() => {
    if (isDrawerOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = originalOverflow
      }
    }

    return undefined
  }, [isDrawerOpen])

  // Close drawer on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDrawerOpen) {
        setIsDrawerOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isDrawerOpen, setIsDrawerOpen])

  if (!mounted) {
    return <></>
  }

  const drawerContainer = document.getElementById('drawer-container')

  if (!drawerContainer) {
    return <></>
  }

  return createPortal(
    <AnimatePresence>
      {isDrawerOpen && (
        <motion.div
          className="fixed left-0 top-0 z-50 flex h-screen w-screen items-stretch justify-end bg-moss-black/[70%] backdrop-blur-[10px]"
          initial={OPACITY_0}
          animate={OPACITY_1}
          exit={OPACITY_0}
        >
          <motion.div
            ref={drawerRef}
            className="flex w-full max-w-full flex-col overflow-hidden bg-moss-white shadow-xl sm:w-auto"
            variants={DRAWER_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{type: 'spring', damping: 30, stiffness: 300}}
          >
            <PlusButton
              onClick={() => {
                setIsDrawerOpen(false)
              }}
              className="absolute right-5 top-5 z-10"
              ariaLabel="Close drawer"
              rotateIcon
            />
            <div className="h-full overflow-y-auto">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    drawerContainer,
  )
}

export default Drawer
